import React, { useId, useMemo } from 'react';

export interface HealthTrendPoint {
  date: string;
  value: number | null;
  display_value?: string;
  status?: string;
}

interface Props {
  data: HealthTrendPoint[];
  unit?: string;
  height?: number;
  compact?: boolean;
  referenceRange?: string;
}

const STATUS_COLORS: Record<string, string> = {
  normal: '#10b981',
  high: '#f59e0b',
  low: '#3b82f6',
  abnormal: '#ef4444',
  unknown: '#94a3b8',
};

function parseRefRange(ref?: string): { low?: number; high?: number } | null {
  if (!ref || !ref.trim()) return null;
  const s = ref.replace(/[～~—–]/g, '-').trim();
  const between = s.match(/([\d.]+)\s*-\s*([\d.]+)/);
  if (between) {
    return { low: parseFloat(between[1]), high: parseFloat(between[2]) };
  }
  const lt = s.match(/[<≤]\s*([\d.]+)/);
  if (lt) return { high: parseFloat(lt[1]) };
  const gt = s.match(/[>≥]\s*([\d.]+)/);
  if (gt) return { low: parseFloat(gt[1]) };
  return null;
}

const HealthMetricTrendChart: React.FC<Props> = ({
  data,
  unit = '',
  height = 200,
  compact = false,
  referenceRange,
}) => {
  const gradId = useId().replace(/:/g, '');
  const numeric = useMemo(
    () =>
      data.filter(
        (d) => d.value != null && Number.isFinite(Number(d.value))
      ) as (HealthTrendPoint & { value: number })[],
    [data]
  );

  if (numeric.length === 0) {
    return (
      <div className="hmtc-empty" style={{ height }}>
        <span>暂无可用数值趋势</span>
        {data.length > 0 && (
          <span className="hmtc-empty-sub">共 {data.length} 次记录（非数值项仅列表展示）</span>
        )}
      </div>
    );
  }

  const pad = compact ? { t: 8, r: 8, b: 22, l: 36 } : { t: 16, r: 12, b: 32, l: 44 };
  const w = 320;
  const h = height;
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;

  const values = numeric.map((d) => d.value);
  const ref = parseRefRange(referenceRange);
  let yMin = Math.min(...values);
  let yMax = Math.max(...values);
  if (ref?.low != null) yMin = Math.min(yMin, ref.low);
  if (ref?.high != null) yMax = Math.max(yMax, ref.high);
  const span = yMax - yMin || 1;
  yMin -= span * 0.08;
  yMax += span * 0.08;

  const xAt = (i: number) =>
    pad.l + (numeric.length === 1 ? innerW / 2 : (i / (numeric.length - 1)) * innerW);
  const yAt = (v: number) => pad.t + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const linePath = numeric
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(d.value)}`)
    .join(' ');

  const areaPath =
    linePath +
    ` L ${xAt(numeric.length - 1)} ${pad.t + innerH} L ${xAt(0)} ${pad.t + innerH} Z`;

  const yTicks = 4;
  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) =>
    yMin + ((yMax - yMin) * i) / yTicks
  );

  return (
    <div className={`hmtc-wrap ${compact ? 'compact' : ''}`}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="hmtc-svg"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="指标趋势图"
      >
        {ref?.low != null && ref?.high != null && (
          <rect
            x={pad.l}
            y={yAt(ref.high)}
            width={innerW}
            height={Math.max(1, yAt(ref.low) - yAt(ref.high))}
            fill="rgba(16,185,129,0.08)"
            stroke="rgba(16,185,129,0.2)"
            strokeWidth="0.5"
          />
        )}
        {yTickVals.map((v, i) => (
          <g key={i}>
            <line
              x1={pad.l}
              y1={yAt(v)}
              x2={pad.l + innerW}
              y2={yAt(v)}
              stroke="rgba(148,163,184,0.12)"
              strokeWidth="0.5"
            />
            {!compact && (
              <text
                x={pad.l - 6}
                y={yAt(v) + 4}
                textAnchor="end"
                fill="rgba(148,163,184,0.55)"
                fontSize="9"
              >
                {v < 10 ? v.toFixed(1) : Math.round(v * 10) / 10}
              </text>
            )}
          </g>
        ))}
        <path d={areaPath} fill={`url(#${gradId})`} opacity="0.35" />
        <path
          d={linePath}
          fill="none"
          stroke="#38bdf8"
          strokeWidth={compact ? 2 : 2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {numeric.map((d, i) => (
          <circle
            key={`${d.date}-${i}`}
            cx={xAt(i)}
            cy={yAt(d.value)}
            r={compact ? 3 : 4.5}
            fill={STATUS_COLORS[d.status || 'unknown'] || '#38bdf8'}
            stroke="#0f172a"
            strokeWidth="1.5"
          />
        ))}
        {!compact &&
          numeric.map((d, i) => (
            <text
              key={`lbl-${i}`}
              x={xAt(i)}
              y={h - 8}
              textAnchor="middle"
              fill="rgba(148,163,184,0.65)"
              fontSize="8"
            >
              {d.date.slice(5).replace('-', '/')}
            </text>
          ))}
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
      </svg>
      {!compact && unit && (
        <div className="hmtc-unit">单位: {unit}</div>
      )}
      <style>{`
        .hmtc-wrap { width: 100%; }
        .hmtc-wrap.compact .hmtc-svg { max-height: ${height}px; }
        .hmtc-svg { width: 100%; height: auto; display: block; }
        .hmtc-unit {
          font-size: 11px; color: rgba(148,163,184,0.55);
          text-align: right; margin-top: 4px; padding-right: 4px;
        }
        .hmtc-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 6px; background: rgba(15,23,42,0.4); border-radius: 14px;
          border: 1px dashed rgba(148,163,184,0.15);
          color: rgba(148,163,184,0.5); font-size: 13px;
        }
        .hmtc-empty-sub { font-size: 11px; opacity: 0.8; }
      `}</style>
    </div>
  );
};

export default HealthMetricTrendChart;
